/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { Box, Divider, Typography } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { useAppSelector } from 'store/hook';
import { useMemo, useState } from 'react';
import {
  Cell,
  Label,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import {
  aggregateAssetWiseData,
  calculatePmVsCmCostFromWorkOrders,
  formatNumberToShortScale,
} from '../utils/dataFormatters';
import {
  getHighestComponentSpend,
  getHighestMaintenanceSpend,
} from '../../CostAnalysis/MaintenanceExpenditure/util';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import componentsImage from 'views/SurvivalAnalysis/Helper/Icons/componenent.png';
import { formatToUSD } from 'views/SurvivalAnalysis/Helper/Function/helper.function';
import { HighestCostingCard } from './HighestCostingCard';
import { truncateTo2dp } from 'utils/helpers/general';
import { Selector } from 'components/Form/Selectors/Selector';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface PieChartDataItem {
  name: string;
  value: number;
  color: string;
}

const CustomPieChart = ({
  data,
  total,
  title,
}: {
  data: PieChartDataItem[];
  total: string;
  title: string;
}) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <Box display="flex" flexDirection="column" gap={1} width={'100%'}>
      {title && (
        <Box>
          <Typography
            sx={{
              color: palette.textSecondary,
            }}
            fontSize={'12px'}
          >
            {title}
          </Typography>
        </Box>
      )}
      <Box height={'150px'}>
        <ResponsiveContainer width={'100%'} height={'100%'}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={70}
              fill="#8884d8"
              cornerRadius={60}
              style={{
                outline: 'none',
              }}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
              ))}
              <Label
                position="center"
                content={() => (
                  <text
                    x="50%"
                    y="50%"
                    fill={palette.textPrimary}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={16}
                    fontWeight="bold"
                  >
                    <tspan>{`$${formatNumberToShortScale(total)}`}</tspan>
                  </text>
                )}
              />
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: enableDarkTheme
                  ? palette.toolBarBackground
                  : palette.boxBackground,
                border: enableDarkTheme
                  ? '1px solid #2F445D'
                  : '1px solid #BDBDBD',
                borderRadius: '4px',
                color: palette.textPrimary,
              }}
              labelStyle={{
                color: palette.textPrimary,
                fontSize: '14px',
                fontWeight: 600,
              }}
              itemStyle={{
                color: palette.textPrimary,
                fontSize: '12px',
              }}
              formatter={(value: number) => `$${truncateTo2dp(value)}`}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      <Box display="flex" flexDirection="row" gap={2}>
        {data.map((entry, index) => (
          <Box
            key={`legend-${index}`}
            display="flex"
            alignItems="center"
            gap={1}
          >
            <Box
              width={8}
              height={8}
              bgcolor={entry.color}
              borderRadius="50%"
            />
            <Box
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              gap={1}
              color={palette.textPrimary}
            >
              {/* Entry Name */}
              <Typography variant="body2" fontSize={12}>
                {entry.name}
              </Typography>

              {/* Formatted Value */}
              <Typography variant="body1" fontSize={12} fontWeight="bold">
                {`$${formatNumberToShortScale(entry.value)}`}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const MaintenanceCostDistribution = () => {
  const { t } = useTranslation();
  const chartView = [
    { display: t('parts_vs_labour'), id: 'parts_vs_labour' },
    { display: t('pm_vs_cm'), id: 'pm_vs_cm' },
  ];
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const assetsData = useAppSelector((state) => state.assetReducer.assets);

  const [filterOption, setFilterOption] = useState('parts_vs_labour');

  const squaresDashboardAssetsData = useAppSelector(
    (state) => state.squaresDashboardReducer.assetDashboard.assets
  );

  const aggregateAssetData = useMemo(() => {
    const temp = aggregateAssetWiseData(squaresDashboardAssetsData);
    return temp;
  }, [squaresDashboardAssetsData]);

  const selectedRangeWorkOrderData = useAppSelector(
    (state) => state.squaresPartsMaintenanceExpenditureReducer.data?.currentData
  );

  const { assetWithHighestCost, componentWithHighestCost } = useMemo(() => {
    if (
      Array.isArray(selectedRangeWorkOrderData) &&
      selectedRangeWorkOrderData?.length
    ) {
      const assetWithHighestCost = getHighestMaintenanceSpend(
        selectedRangeWorkOrderData
      );
      const componentWithHighestCost = getHighestComponentSpend(
        selectedRangeWorkOrderData
      );

      const assetWithHighestCostInfo = assetsData.find(
        (el) => el.bumperNumber === assetWithHighestCost.assetWithHighestSpend
      );

      const assetSpendMap = selectedRangeWorkOrderData.reduce(
        (acc, workOrder) => {
          const assetId = workOrder.asset_id;
          if (!acc[assetId]) {
            acc[assetId] = 0;
          }
          acc[assetId] += workOrder.total_cost || 0;
          return acc;
        },
        {}
      );

      const allAssetsWithHighestCost = Object.entries(assetSpendMap)
        .filter(
          ([_, totalSpend]) => totalSpend === assetWithHighestCost.totalSpend
        )
        .map(([assetId, _]) => assetId);

      const componentSpendMap = selectedRangeWorkOrderData.reduce(
        (acc, workOrder) => {
          const componentName = workOrder.components_category;
          if (!acc[componentName]) {
            acc[componentName] = 0;
          }
          acc[componentName] += workOrder.total_cost || 0;
          return acc;
        },
        {}
      );

      const allComponentsWithHighestCost = Object.entries(componentSpendMap)
        .filter(
          ([_, totalSpend]) =>
            totalSpend === componentWithHighestCost.totalSpend
        )
        .map(([componentName, _]) => componentName);

      return {
        assetWithHighestCost: {
          bumperNumber: assetWithHighestCost.assetWithHighestSpend,
          cost: assetWithHighestCost.totalSpend,
          icon:
            assetWithHighestCostInfo?.make && assetWithHighestCostInfo?.model
              ? getImageNameByMakeModelAndAsset(
                  assetWithHighestCostInfo?.make,
                  assetWithHighestCostInfo?.model,
                  assetWithHighestCostInfo?.assetType,
                  'Small'
                )
              : null,
          makeModel: `${assetWithHighestCostInfo?.make}-${assetWithHighestCostInfo?.model}`,
          assetIds: allAssetsWithHighestCost,
        },
        componentWithHighestCost: {
          name: componentWithHighestCost.componentWithHighestSpend,
          cost: componentWithHighestCost.totalSpend,
          icon: componentsImage,
          componentIds: allComponentsWithHighestCost,
        },
      };
    } else {
      return {
        assetWithHighestCost: {
          bumperNumber: '',
          cost: 0,
          icon: '',
          makeModel: '',
          assetIds: [],
        },
        componentWithHighestCost: {
          name: '',
          cost: 0,
          icon: '',
          componentIds: [],
        },
      };
    }
  }, [selectedRangeWorkOrderData]);

  const { totalCmCost, totalPmCost } = useMemo(() => {
    return calculatePmVsCmCostFromWorkOrders(selectedRangeWorkOrderData);
  }, [selectedRangeWorkOrderData]);

  const pmVsCmCostPieChartData = [
    { name: 'PM', value: totalPmCost, color: '#A776FF' },
    { name: 'CM', value: totalCmCost, color: '#FFA403' },
  ];
  const partVsLaborCostPieChartData = [
    {
      name: t('parts'),
      value: aggregateAssetData.total_part_cost,
      color: palette.primary,
    },
    {
      name: t('labour'),
      value: aggregateAssetData.total_labour_cost,
      color: '#58BF6A',
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? palette.toolBarBackground
          : palette.toolBarBackground,
        borderRadius: '5px',
        border: enableDarkTheme ? '1px solid #2F445D' : '1px solid #BDBDBD',
      }}
      p={1}
      display={'flex'}
      flexDirection={'column'}
      gap={1}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            color: palette.textPrimary,
            fontWeight: 'bolder',
          }}
        >
          {t('maintenance_cost_distribution')}
        </Typography>
        <Selector
          value={filterOption}
          selectorOptions={chartView}
          onChange={(e) => {
            setFilterOption(e.target.value as string);
          }}
          enableDarkTheme={enableDarkTheme}
          minWidth="150px"
        />
      </Box>

      <Box
        display={'flex'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-around'}
        gap={2}
      >
        {filterOption === 'parts_vs_labour' && (
          <CustomPieChart
            title=""
            data={partVsLaborCostPieChartData}
            total={`${
              Number(aggregateAssetData.total_part_cost) +
              Number(aggregateAssetData.total_labour_cost)
            }`}
          />
        )}

        {filterOption === 'pm_vs_cm' && (
          <CustomPieChart
            title=""
            data={pmVsCmCostPieChartData}
            total={`${totalPmCost + totalCmCost}`}
          />
        )}
      </Box>
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          height: '2px',
          background: enableDarkTheme ? '#2F445D' : '#9E9E9E',
        }}
      />
      <Box
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        gap={2}
        justifyContent={'space-between'}
      >
        <HighestCostingCard
          title={t('highest_costing_asset')}
          name={assetWithHighestCost.bumperNumber}
          subName={assetWithHighestCost?.makeModel}
          value={`$${formatToUSD(assetWithHighestCost?.cost, '')}`}
          icon={assetWithHighestCost.icon}
          usePngComponent
          otherIds={assetWithHighestCost.assetIds}
        />
        {/* <Divider
          orientation="horizontal"
          flexItem
          sx={{
            width: '2px',
            background: enableDarkTheme ? '#2F445D' : '#9E9E9E',
          }}
        /> */}
        <HighestCostingCard
          title={t('highest_costing_component')}
          name={componentWithHighestCost?.name}
          subName={null}
          value={`$${formatToUSD(componentWithHighestCost.cost, '')}`}
          icon={componentWithHighestCost.icon}
          usePngComponent={false}
          otherIds={componentWithHighestCost.componentIds}
        />
      </Box>
    </Box>
  );
};
